/**
 * Utilities for handling geolocation restrictions
 */

/**
 * Check if a lesson is accessible from the current location
 * @param {Object} lesson - The lesson object
 * @param {Object} userLocation - User's location data { country: 'US', ip: '192.168.1.1' }
 * @returns {Object} - { allowed: true/false, message: 'Optional message if blocked' }
 */
export const checkGeoRestrictions = (lesson, userLocation) => {
  // If no restrictions or restrictions not enabled, allow access
  if (!lesson.geo_restrictions || !lesson.geo_restrictions.enabled) {
    return { allowed: true };
  }

  const restrictions = lesson.geo_restrictions;
  let allowed = true;
  
  // Check country restrictions
  if (restrictions.countries && restrictions.countries.length > 0 && userLocation.country) {
    if (restrictions.restrictionType === 'allowlist') {
      // Allowlist: Only allow if country is in the list
      allowed = restrictions.countries.includes(userLocation.country);
    } else {
      // Blocklist: Block if country is in the list
      allowed = !restrictions.countries.includes(userLocation.country);
    }
  }
  
  // Check IP range restrictions if applicable and not already blocked
  if (allowed && restrictions.ipRanges && restrictions.ipRanges.length > 0 && userLocation.ip) {
    for (const range of restrictions.ipRanges) {
      if (isIpInRange(userLocation.ip, range.start, range.end)) {
        // If allowlist, this is a pass
        if (restrictions.restrictionType === 'allowlist') {
          return { allowed: true };
        }
        // If blocklist, this is blocked
        return { 
          allowed: false, 
          message: restrictions.blockMessage || 'This content is not available in your region.'
        };
      }
    }
    
    // For allowlist, if IP didn't match any range, block it
    if (restrictions.restrictionType === 'allowlist' && restrictions.ipRanges.length > 0) {
      allowed = false;
    }
  }
  
  return {
    allowed,
    message: allowed ? '' : (restrictions.blockMessage || 'This content is not available in your region.')
  };
};

/**
 * Check if an IP is within a given range
 * @param {string} ip - IP to check
 * @param {string} start - Start of IP range
 * @param {string} end - End of IP range
 * @returns {boolean}
 */
export const isIpInRange = (ip, start, end) => {
  // Convert IPs to numbers for comparison
  const ipNum = ipToNumber(ip);
  const startNum = ipToNumber(start);
  const endNum = ipToNumber(end);
  
  return ipNum >= startNum && ipNum <= endNum;
};

/**
 * Convert IP address to a number for easier comparison
 * @param {string} ip - IP address (e.g. "192.168.1.1")
 * @returns {number}
 */
export const ipToNumber = (ip) => {
  if (!ip) return 0;
  
  const parts = ip.split('.');
  if (parts.length !== 4) return 0;
  
  return ((parseInt(parts[0], 10) << 24) |
          (parseInt(parts[1], 10) << 16) |
          (parseInt(parts[2], 10) << 8) |
          parseInt(parts[3], 10)) >>> 0;
};

/**
 * Fetch the user's location information
 * @returns {Promise<Object>} - User location data
 */
export const getUserLocation = async () => {
  try {
    // Use a geolocation service API
    const response = await fetch('https://api.ipgeolocation.io/ipgeo?apiKey=YOUR_API_KEY');
    const data = await response.json();
    
    return {
      country: data.country_code2,
      ip: data.ip
    };
  } catch (error) {
    console.error('Error fetching user location:', error);
    return { country: null, ip: null };
  }
};

/**
 * Apply geolocation restrictions to a list of lessons
 * @param {Array} lessons - List of lessons to filter
 * @returns {Promise<Array>} - Filtered list of accessible lessons
 */
export const filterAccessibleLessons = async (lessons) => {
  const userLocation = await getUserLocation();
  
  return lessons.filter(lesson => {
    const { allowed } = checkGeoRestrictions(lesson, userLocation);
    return allowed;
  });
}; 